import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profilesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  User, 
  Pencil, 
  Trash2, 
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  ShoppingBag,
  FileText,
  Shield
} from 'lucide-react';

export const Profiles = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    business_description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    tax_id: '',
    insurance_info: '',
    website_url: '',
    instagram_url: '',
    facebook_url: '',
    etsy_url: '',
    other_social_url: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfiles();
  }, [user, navigate]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await profilesAPI.getAll();
      setProfiles(response.data);
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        name: profile.name || '',
        business_name: profile.business_name || '',
        business_description: profile.business_description || '',
        contact_name: profile.contact_name || '',
        contact_email: profile.contact_email || '',
        contact_phone: profile.contact_phone || '',
        address_street: profile.address_street || '',
        address_city: profile.address_city || '',
        address_state: profile.address_state || '',
        address_zip: profile.address_zip || '',
        tax_id: profile.tax_id || '',
        insurance_info: profile.insurance_info || '',
        website_url: profile.website_url || '',
        instagram_url: profile.instagram_url || '',
        facebook_url: profile.facebook_url || '',
        etsy_url: profile.etsy_url || '',
        other_social_url: profile.other_social_url || '',
      });
    } else {
      setEditingProfile(null);
      setFormData({
        name: '',
        business_name: '',
        business_description: '',
        contact_name: user?.name || '',
        contact_email: user?.email || '',
        contact_phone: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_zip: '',
        tax_id: '',
        insurance_info: '',
        website_url: '',
        instagram_url: '',
        facebook_url: '',
        etsy_url: '',
        other_social_url: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.business_name || !formData.contact_name) {
      toast.error('Profile name, business name, and contact name are required');
      return;
    }

    const payload = {
      ...formData,
      business_description: formData.business_description || null,
      contact_email: formData.contact_email || null,
      contact_phone: formData.contact_phone || null,
      address_street: formData.address_street || null,
      address_city: formData.address_city || null,
      address_state: formData.address_state || null,
      address_zip: formData.address_zip || null,
      tax_id: formData.tax_id || null,
      insurance_info: formData.insurance_info || null,
      website_url: formData.website_url || null,
      instagram_url: formData.instagram_url || null,
      facebook_url: formData.facebook_url || null,
      etsy_url: formData.etsy_url || null,
      other_social_url: formData.other_social_url || null,
    };

    try {
      if (editingProfile) {
        await profilesAPI.update(editingProfile.id, payload);
        toast.success('Profile updated');
      } else {
        await profilesAPI.create(payload);
        toast.success('Profile created');
      }
      setIsDialogOpen(false);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      await profilesAPI.delete(id);
      toast.success('Profile deleted');
      fetchProfiles();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Application Profiles</h1>
            <p className="text-gray-600 mt-1">Manage business profiles for event applications</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()} 
            className="btn-primary"
            data-testid="add-profile-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Profile
          </Button>
        </div>

        {/* Profiles Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : profiles.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No profiles yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first application profile to quickly apply for events
            </p>
            <Button onClick={() => handleOpenDialog()} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Profile
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard hover:shadow-hard-hover transition-all"
                data-testid={`profile-card-${profile.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-[#1E3A5F] text-lg">{profile.name}</h3>
                    <p className="text-sm text-gray-500">{profile.business_name}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(profile)}
                      data-testid={`edit-profile-${profile.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(profile.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-profile-${profile.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {profile.business_description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{profile.business_description}</p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="w-4 h-4" />
                    <span>{profile.contact_name}</span>
                  </div>
                  {profile.contact_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{profile.contact_email}</span>
                    </div>
                  )}
                  {profile.contact_phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{profile.contact_phone}</span>
                    </div>
                  )}
                  {(profile.address_city || profile.address_state) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{[profile.address_city, profile.address_state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  {profile.website_url && (
                    <a href={profile.website_url} target="_blank" rel="noopener noreferrer" 
                       className="p-2 rounded-lg bg-gray-100 hover:bg-[#FFEDDF] transition-colors">
                      <Globe className="w-4 h-4 text-[#1E3A5F]" />
                    </a>
                  )}
                  {profile.instagram_url && (
                    <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer"
                       className="p-2 rounded-lg bg-gray-100 hover:bg-[#FFEDDF] transition-colors">
                      <Instagram className="w-4 h-4 text-[#1E3A5F]" />
                    </a>
                  )}
                  {profile.facebook_url && (
                    <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer"
                       className="p-2 rounded-lg bg-gray-100 hover:bg-[#FFEDDF] transition-colors">
                      <Facebook className="w-4 h-4 text-[#1E3A5F]" />
                    </a>
                  )}
                  {profile.etsy_url && (
                    <a href={profile.etsy_url} target="_blank" rel="noopener noreferrer"
                       className="p-2 rounded-lg bg-gray-100 hover:bg-[#FFEDDF] transition-colors">
                      <ShoppingBag className="w-4 h-4 text-[#1E3A5F]" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">
              {editingProfile ? 'Edit Profile' : 'Create Profile'}
            </DialogTitle>
            <DialogDescription>
              {editingProfile 
                ? 'Update your application profile details' 
                : 'Create a reusable profile for event applications'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Name */}
            <div className="p-4 bg-[#FFEDDF] rounded-lg">
              <Label htmlFor="name" className="text-[#1E3A5F] font-semibold">Profile Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Jewelry Shows, Craft Fairs"
                className="mt-1"
                required
                data-testid="profile-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">Use this to identify different profiles for different event types</p>
            </div>

            {/* Business Info */}
            <div>
              <h4 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Business Information
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                    placeholder="Your business name"
                    required
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="business_description">Business Description</Label>
                  <Textarea
                    id="business_description"
                    value={formData.business_description}
                    onChange={(e) => setFormData({...formData, business_description: e.target.value})}
                    placeholder="Brief description of your business and products..."
                    rows={3}
                    data-testid="business-description-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Contact Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="contact_phone">Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h4 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Address
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="address_street">Street Address</Label>
                  <Input
                    id="address_street"
                    value={formData.address_street}
                    onChange={(e) => setFormData({...formData, address_street: e.target.value})}
                    data-testid="address-street-input"
                  />
                </div>
                <div>
                  <Label htmlFor="address_city">City</Label>
                  <Input
                    id="address_city"
                    value={formData.address_city}
                    onChange={(e) => setFormData({...formData, address_city: e.target.value})}
                    data-testid="address-city-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="address_state">State</Label>
                    <Input
                      id="address_state"
                      value={formData.address_state}
                      onChange={(e) => setFormData({...formData, address_state: e.target.value})}
                      data-testid="address-state-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_zip">ZIP</Label>
                    <Input
                      id="address_zip"
                      value={formData.address_zip}
                      onChange={(e) => setFormData({...formData, address_zip: e.target.value})}
                      data-testid="address-zip-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Legal/Business */}
            <div>
              <h4 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Legal & Insurance
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tax_id">Tax ID / EIN</Label>
                  <Input
                    id="tax_id"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                    placeholder="XX-XXXXXXX"
                    data-testid="tax-id-input"
                  />
                </div>
                <div>
                  <Label htmlFor="insurance_info">Insurance Info</Label>
                  <Input
                    id="insurance_info"
                    value={formData.insurance_info}
                    onChange={(e) => setFormData({...formData, insurance_info: e.target.value})}
                    placeholder="Policy number or provider"
                    data-testid="insurance-input"
                  />
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-[#1E3A5F] mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Online Presence
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="website_url">Website</Label>
                  <Input
                    id="website_url"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                    placeholder="https://..."
                    data-testid="website-input"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram_url">Instagram</Label>
                  <Input
                    id="instagram_url"
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                    placeholder="https://instagram.com/..."
                    data-testid="instagram-input"
                  />
                </div>
                <div>
                  <Label htmlFor="facebook_url">Facebook</Label>
                  <Input
                    id="facebook_url"
                    type="url"
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({...formData, facebook_url: e.target.value})}
                    placeholder="https://facebook.com/..."
                    data-testid="facebook-input"
                  />
                </div>
                <div>
                  <Label htmlFor="etsy_url">Etsy Shop</Label>
                  <Input
                    id="etsy_url"
                    type="url"
                    value={formData.etsy_url}
                    onChange={(e) => setFormData({...formData, etsy_url: e.target.value})}
                    placeholder="https://etsy.com/shop/..."
                    data-testid="etsy-input"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-profile-btn">
                {editingProfile ? 'Update' : 'Create'} Profile
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profiles;
